import React from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Box, Container, Typography, Breadcrumbs, Link, Grid} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const NewsPage = () => {
  const { newsId } = useParams();

  const navigate = useNavigate();

  // This would typically come from an API or database
  const newsItems = {
    'next-gen-enterprise': {
      title: 'Next Generation Enterprise Solutions',
      date: 'February 7, 2025',
      image: '/images/news-enterprise.jpg',
      description: 'Introducing our latest enterprise software solutions with advanced AI capabilities.',
      fullContent: `Our company is proud to announce the launch of our next-generation enterprise solutions, 
      incorporating cutting-edge artificial intelligence and machine learning capabilities. This revolutionary 
      platform represents a significant leap forward in enterprise software technology.

      Key Features:
      • Advanced AI-powered analytics
      • Real-time decision support systems
      • Automated workflow optimization
      • Predictive maintenance capabilities
      • Enhanced security protocols

      The new solution has been developed in partnership with leading industry experts and has undergone 
      extensive testing with select enterprise clients. Early adopters have reported significant improvements 
      in operational efficiency and decision-making processes.

      "This represents a major milestone in enterprise software evolution," says our Chief Technology Officer. 
      "We're not just improving existing systems; we're fundamentally changing how enterprises can leverage 
      technology for better business outcomes."

      The platform will be available to enterprise clients starting next month, with a phased rollout planned 
      across different regions.`
    },
    'cloud-expansion': {
      title: 'Cloud Infrastructure Expansion',
      date: 'February 5, 2025',
      image: '/images/news-cloud.jpg',
      description: 'Expanding our global cloud infrastructure with new data centers in Asia and Europe.',
      fullContent: `We are excited to announce a major expansion of our global cloud infrastructure, with new 
      state-of-the-art data centers being established in key locations across Asia and Europe. This expansion 
      marks a significant milestone in our commitment to providing world-class cloud services to our global clientele.

      The new data centers will be located in:
      • Singapore
      • Tokyo, Japan
      • Frankfurt, Germany
      • Stockholm, Sweden

      Each facility has been designed with sustainability in mind, utilizing renewable energy sources and 
      implementing advanced cooling systems to minimize environmental impact while maintaining optimal performance.

      This expansion will provide our clients with:
      • Reduced latency for regional users
      • Enhanced data sovereignty compliance
      • Improved disaster recovery capabilities
      • Increased storage and computing capacity

      The first phase of the expansion will be completed by Q3 2025, with full operational capability 
      expected by the end of the year.`
    },
    'security-partnership': {
      title: 'Cybersecurity Partnership',
      date: 'February 3, 2025',
      image: '/images/news-security.jpg',
      description: 'Strategic partnership to enhance cybersecurity solutions for enterprise customers.',
      fullContent: `We are proud to announce a strategic partnership with leading cybersecurity firm SecureNet 
      to enhance our enterprise security solutions. This collaboration brings together our enterprise expertise 
      with SecureNet's advanced threat detection and prevention capabilities.

      The partnership will focus on:
      • Advanced threat detection and response
      • Zero-trust architecture implementation
      • AI-powered security analytics
      • Automated incident response
      • Comprehensive security training programs

      This collaboration comes at a crucial time when cybersecurity threats are becoming increasingly sophisticated. 
      Our joint solution will provide enterprise customers with:
      • Real-time threat monitoring
      • Automated security responses
      • Enhanced data protection
      • Compliance management tools
      • Regular security assessments

      The integrated security solution will be available to enterprise customers starting Q2 2025, with early 
      access programs beginning next month.`
    }
  };

  const news = newsItems["security-partnership"];

  if (!news) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          pt: 10,
          pb: 6,
          backgroundColor: '#000000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4" sx={{ color: '#FFD700' }}>
          News article not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pt: 10,
        pb: 6,
        backgroundColor: '#000000',
      }}
    >
      <Container maxWidth="lg">
        <Breadcrumbs
          sx={{
            mb: 4,
            '& .MuiBreadcrumbs-separator': {
              color: 'rgba(255, 215, 0, 0.5)',
            },
          }}
        >
          <Link
            component={RouterLink}
            to="/"
            sx={{
              color: 'rgba(255, 215, 0, 0.7)',
              textDecoration: 'none',
              '&:hover': {
                color: '#FFD700',
              },
            }}
          >
            Home
          </Link>
          <Typography sx={{ color: '#FFD700' }}>News</Typography>
        </Breadcrumbs>

        {
          Object.keys(newsItems).map((url, index) =>
            <Box
              key={index}
              sx={{
                backgroundColor: 'rgba(255, 248, 225, 0.97)',
                borderRadius: 2,
                overflow: 'hidden',
                marginBottom: "30px"
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  overflow: 'hidden',
                  p: "25px"
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={3}>
                    <img
                      src={"https://library.ceu.edu/wp-content/uploads/news-2444778_960_720.jpg"}
                      alt={newsItems[url].title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        mb: 2,
                      }}
                    >
                      {newsItems[url].date}
                    </Typography>
                    <Typography
                      variant="h3"
                      component="h1"
                      sx={{
                        color: '#000000',
                        mb: 4,
                      }}
                    >
                      {newsItems[url].title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.8)',
                        whiteSpace: 'pre-line',
                        lineHeight: 1.8,
                      }}
                    >
                      {newsItems[url].fullContent.substring(0, 200) + "...."}
                    </Typography>
                    <a href={`news/${url}`}>Read More</a>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )
        }
      </Container>
    </Box>
  );
};

export default NewsPage;
